import React, { useState } from "react";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { deleteTemplate, deleteLibraryTemplate } from "../../api";
import "../Accounts/acc-list.css";

/**
 * TemplateDeleteModal — irreversible deletion of a template.
 *
 * Follows /CLAUDE.md §1.8.1 (destructive confirmation modals): warning alert
 * with the consequence in bold, `danger` commit button with a trash icon and a
 * matched-width `outline-secondary` Cancel, spinner in place of the label while
 * submitting. No free-text field here, so there is no close-confirmation
 * interstitial to suppress.
 *
 * `scope` picks the route: practice templates delete through /templates/:id,
 * library templates through /templates/library/:id (SystemAdmin only).
 */
export default function TemplateDeleteModal({ show, onHide, template, scope = "practice", onDeleted }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isLibrary = scope === "system";

  const handleDelete = async () => {
    setSubmitting(true);
    setError("");
    try {
      if (isLibrary) await deleteLibraryTemplate(String(template._id));
      else await deleteTemplate(String(template._id));
      onDeleted(template);
    } catch (err) {
      setError(err.message || "Failed to delete the template.");
      setSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} centered>
      <Modal.Header
        closeButton={!submitting}
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Delete Template — {template.templateTitle}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          This <strong>permanently deletes</strong> the template. Notes already written
          against it keep the content that was captured, but the template itself cannot be
          recovered.
        </Alert>

        {isLibrary && (
          <p className="small text-muted mb-0">
            Practices that already took a copy of this library template keep theirs — those
            are separate templates.
          </p>
        )}

        {error && <Alert variant="danger" className="mt-3 mb-0 py-2 small">{error}</Alert>}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          {submitting ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <>
              <i className="fa-solid fa-trash me-2" />
              Delete
            </>
          )}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={submitting}
          style={{ minWidth: 140 }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
